/**
 * Horizontal "slat" platform mark: stacked bars with taper + stagger (diamond / layered read).
 * Central bands are slightly thicker; opacity falls toward the ends for depth.
 */

/** Width factors (symmetric); twin peak at the centre for a clear focal band. */
export const PLATFORM_SLAT_FACTORS = [0.33, 0.5, 0.71, 0.91, 0.91, 0.71, 0.5, 0.33];

/** Horizontal offset (hero px); smooth alternating shear. */
export const PLATFORM_SLAT_STAGGER = [7, -5.5, 5, -3.5, 0, 3.5, -5, 6.5];

/** Slightly taller centre slats for hierarchy. */
export const PLATFORM_SLAT_HEIGHT_MUL = [1, 1, 1.06, 1.13, 1.13, 1.06, 1, 1];

/** Vignette-style opacity — edges softer, centre full. */
export const PLATFORM_SLAT_OPACITY = [0.82, 0.88, 0.94, 1, 1, 0.94, 0.88, 0.82];

/**
 * @param {object} opts
 * @param {number} opts.width - viewBox width
 * @param {number} opts.height - viewBox height
 * @param {number} opts.cx - horizontal center
 * @param {number} opts.maxW - max bar width
 * @param {number} opts.barH - base bar thickness (before height mul)
 * @param {number} opts.gap - vertical gap between bars
 * @param {number} [opts.staggerScale=1] - multiply stagger (mini marks)
 * @returns {{ x: number, y: number, w: number, h: number, rx: number, opacity: number }[]}
 */
export function buildPlatformSlatRects({
  width,
  height,
  cx,
  maxW,
  barH,
  gap,
  staggerScale = 1,
}) {
  const n = PLATFORM_SLAT_FACTORS.length;
  const heights = PLATFORM_SLAT_HEIGHT_MUL.map((m) => barH * m);
  const totalH = heights.reduce((sum, h) => sum + h, 0) + (n - 1) * gap;
  const y0 = (height - totalH) / 2;

  let y = y0;
  return PLATFORM_SLAT_FACTORS.map((f, i) => {
    const h = heights[i];
    const w = maxW * f;
    const stagger = PLATFORM_SLAT_STAGGER[i] * staggerScale;
    const rx = Math.min(3.2, h * 0.32);
    const rect = {
      x: cx - w / 2 + stagger,
      y,
      w,
      h,
      rx,
      opacity: PLATFORM_SLAT_OPACITY[i],
    };
    y += h + gap;
    return rect;
  });
}
